import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft, Wallet, Calendar, FileText, Eye, Download, Loader2, Inbox, AlertCircle, RefreshCw,
} from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { payrollService } from '../services/payrollService';

const FILE_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const TURKISH_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const resolveFileUrl = (value?: string) => {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${FILE_BASE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

interface PayrollRow {
  id: string | number;
  payroll_year: number;
  payroll_month: number;
  file_path: string;
  file_name?: string;
  mime_type?: string;
  file_size?: number;
  created_at?: string;
}

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const periodLabel = (year: number, month: number) => {
  const m = Number(month);
  const name = TURKISH_MONTHS[m - 1] || String(month);
  return `${name} ${year}`;
};

export function DriverMobilePayrolls() {
  const navigate = useNavigate();
  const [driver, setDriver] = useState<any>(null);
  const [payrolls, setPayrolls] = useState<PayrollRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const loadPayrolls = async (asRefresh = false) => {
    if (asRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await payrollService.getMyPayrolls();
      const data = (response as any).data;
      setDriver(data?.driver || null);
      setPayrolls(Array.isArray(data?.payrolls) ? data.payrolls : []);
    } catch (error: any) {
      console.error('Error loading payrolls:', error);
      setError(error?.message || 'Bordrolar yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const userData = sessionStorage.getItem('driverMobileUser');
    if (!userData) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    loadPayrolls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const openFile = (payroll: PayrollRow) => {
    const url = resolveFileUrl(payroll.file_path);
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  };

  const downloadFile = async (payroll: PayrollRow) => {
    const url = resolveFileUrl(payroll.file_path);
    if (!url) return;
    setDownloading(String(payroll.id));
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('İndirme başarısız');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = payroll.file_name || `bordro-${periodLabel(payroll.payroll_year, payroll.payroll_month)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      console.error('Download error:', downloadError);
      openFile(payroll);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-600 dark:to-blue-700 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Maaş Bordroları</h1>
          <button
            onClick={() => loadPayrolls(true)}
            disabled={refreshing}
            className="rounded-xl bg-slate-100 p-2 text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 disabled:opacity-50 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
            aria-label="Yenile"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center dark:bg-white/15 dark:text-white">
            <Wallet className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{driver?.full_name || '-'}</p>
            <p className="text-xs text-slate-500 dark:text-blue-100">
              {[driver?.cekici_plaka, driver?.dorse_plaka].filter(Boolean).join(' · ') || 'Özmal sürücü'}
            </p>
          </div>
          <div className="ml-auto text-center">
            <p className="text-2xl font-bold text-blue-700 dark:text-white">{loading ? '-' : payrolls.length}</p>
            <p className="text-xs text-slate-500 dark:text-blue-100">Bordro</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-3 pb-28">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="mt-3 text-sm">Bordrolar yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-red-700">{error}</p>
            <p className="mt-1 text-xs text-red-600">Bordro kaydınız için operasyon ekibiyle iletişime geçebilirsiniz.</p>
          </div>
        ) : payrolls.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-[#111111]">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Henüz bordro yüklenmemiş</p>
            <p className="mt-1 text-xs text-slate-500">Bordro dosyası yüklendiğinde burada görüntüleyebileceksiniz.</p>
          </div>
        ) : (
          payrolls.map((payroll) => {
            const label = periodLabel(payroll.payroll_year, payroll.payroll_month);
            return (
              <div key={String(payroll.id)} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 dark:bg-white/15 dark:text-white">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-300">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="truncate">{payroll.file_name || 'Maaş bordrosu'}</span>
                      <span className="text-slate-400">·</span>
                      <span>{formatFileSize(payroll.file_size)}</span>
                    </p>
                    {payroll.created_at && (
                      <p className="mt-0.5 text-[11px] text-slate-400">Yüklenme: {new Date(payroll.created_at.replace(' ', 'T')).toLocaleDateString('tr-TR')}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => openFile(payroll)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                  >
                    <Eye className="w-4 h-4" />
                    Görüntüle
                  </button>
                  <button
                    onClick={() => downloadFile(payroll)}
                    disabled={downloading === String(payroll.id)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 disabled:opacity-50 dark:bg-white/10 dark:text-white dark:ring-white/15 dark:hover:bg-white/15"
                  >
                    {downloading === String(payroll.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    İndir
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <DriverBottomNav />
    </div>
  );
}